/**
 * Reading a failed API response like a human.
 *
 * `apiRequest` (WorkSheetContext) rejects with `Error('API <status>: <raw body>')`
 * and that FORMAT IS LOAD-BEARING — `serviceMode.js#isServiceFeatureDisabledError`,
 * the completion sheet's out-of-credits check and several screens match on it —
 * so it stays exactly as it is. What was missing is everything a screen actually
 * needs to talk to the user: the status, the machine code and the server's own
 * sentence. Those now ride along as properties on the error, and this module
 * turns them into something showable.
 *
 * Without it, a rejected quote edit put
 * `API 422: {"error":"…","msg":"QUOTE_VALIDATION_FAILED","fields":["items.0.name"]}`
 * on screen verbatim.
 */

/** Machine code the backend sends for a record that only exists on a client. */
export const UNSYNCED_API_CODE = 'UNSYNCED_CLIENT_ID';

/**
 * Parse the JSON body an API error carries, if it has one.
 *
 * @param {Error & {payload?: Object, body?: string}} err Rejected error.
 * @returns {Object|null} The parsed body, or `null` when there is none.
 */
export const apiErrorPayload = (err) => {
    if (err && err.payload && typeof err.payload === 'object') {
        return err.payload;
    }

    // Older throw sites only have the formatted message — dig the JSON out of it.
    const raw = String(err?.body || err?.message || '');
    const start = raw.indexOf('{');

    if (start < 0) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw.slice(start));

        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_parseError) {
        return null;
    }
};

/**
 * HTTP status of a failed request.
 *
 * @param {Error & {status?: number}} err Rejected error.
 * @returns {number} The status, or `0` when it cannot be determined.
 */
export const apiErrorStatus = (err) => {
    if (err && Number.isFinite(err.status)) {
        return err.status;
    }

    const match = /^API (\d{3})/.exec(String(err?.message || ''));

    return match ? Number(match[1]) : 0;
};

/**
 * Machine error code, whichever of the API's two conventions it arrived under
 * (`code` in the service module, `msg` in quotes/CRM).
 *
 * @param {Error & {code?: string}} err Rejected error.
 * @returns {string} The code, or `''`.
 */
export const apiErrorCode = (err) => {
    if (err && typeof err.code === 'string' && err.code) {
        return err.code;
    }

    const payload = apiErrorPayload(err);
    const code = payload?.code || payload?.msg;

    return typeof code === 'string' ? code : '';
};

/**
 * The sentence to show the user for a failed request.
 *
 * Prefers the server's own explanation — it is the only party that knows WHY
 * ("some of its data is invalid", "feature disabled", "file too large") — and
 * falls back to the caller's localized text rather than to a raw `API 5xx: {…}`
 * dump, which means nothing to a technician.
 *
 * @param {Error} err Rejected error.
 * @param {string} [fallback=''] Localized fallback message.
 * @returns {string} Message for the UI.
 */
export const describeApiError = (err, fallback = '') => {
    const payload = apiErrorPayload(err);
    const serverMessage = [payload?.error, payload?.message]
        .find((value) => typeof value === 'string' && value.trim());

    if (serverMessage) {
        const fields = Array.isArray(payload?.fields) ? payload.fields.filter(Boolean) : [];

        return fields.length ? `${serverMessage} (${fields.join(', ')})` : serverMessage;
    }

    if (fallback) {
        return fallback;
    }

    return String(err?.message || '');
};

export default {
    UNSYNCED_API_CODE,
    apiErrorPayload,
    apiErrorStatus,
    apiErrorCode,
    describeApiError,
};
